import { useState, type FormEvent } from 'react';
import { api } from '../lib/api.js';
import { LoginTokensPanel } from '../components/LoginTokensPanel.js';

export function AccountPage({
  email,
  name,
  role,
  hasPassword,
  onChanged,
}: {
  email: string;
  name?: string | null;
  role?: string;
  hasPassword: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function onPassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    if (next !== confirm) {
      setMsg({ ok: false, text: 'New passwords do not match.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.changePassword(next, hasPassword ? current : undefined);
      setCurrent('');
      setNext('');
      setConfirm('');
      setMsg({ ok: true, text: 'Password updated. Other browsers were signed out.' });
      onChanged();
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      setMsg({ ok: false, text: m === 'invalid_current_password' ? 'Current password is incorrect.' : m });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="page-title">Account</h1>
      <p className="page-subtitle">
        Signed in as <strong>{name ? `${name} (${email})` : email}</strong>
        {role ? ` · ${role}` : ''}
      </p>
      <div className="account-grid">
        <section className="panel">
          <div className="panel__title">{hasPassword ? 'Change password' : 'Set a password'}</div>
          <form className="auth-form" style={{ maxWidth: 380 }} onSubmit={(e) => void onPassword(e)}>
            <input type="email" autoComplete="username" value={email} readOnly hidden />
            {hasPassword && (
              <div className="form-row">
                <label className="form-label" htmlFor="curPw">Current password</label>
                <input id="curPw" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
              </div>
            )}
            <div className="form-row">
              <label className="form-label" htmlFor="newPw">New password</label>
              <input id="newPw" type="password" autoComplete="new-password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} required />
              <span className="form-help">At least 10 characters.</span>
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="newPw2">Confirm new password</label>
              <input id="newPw2" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            {msg && <div className={msg.ok ? 'form-success' : 'form-error'}>{msg.text}</div>}
            <div>
              <button type="submit" className="btn" style={{ width: 'auto' }} disabled={busy || !next || !confirm}>
                {busy ? 'Saving…' : hasPassword ? 'Update password' : 'Set password'}
              </button>
            </div>
          </form>
        </section>

        <section className="panel">
          <div className="panel__title">My login tokens</div>
          <p className="form-help" style={{ marginTop: 0 }}>
            Set a short memorable token (like a team nickname) or generate a long random one. It signs you
            straight into your account via “Sign in with team token” or a one-click link. One token is
            usually all you need; revoke it any time.
          </p>
          <LoginTokensPanel
            load={() => api.myTokens()}
            create={(label, token) => api.createMyToken(label, token)}
            revoke={(id) => api.revokeMyToken(id)}
          />
        </section>
      </div>
    </div>
  );
}
